import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRFQMatchedListings } from '@steelbuy/data-access';
import { MaterialType } from '@steelbuy/ts-shared';
import {
    ButtonGhostSystemOnLightM,
    ButtonPrimary,
    FormActionbar,
    LoadingSpinner,
    MarketingBannerContact,
    NotificationIllustrated,
    NotificationIllustratedAlignment,
    NotificationIllustratedItem,
    useWizardStepsContext,
} from '@steelbuy/ui-primitive';
import { isRequestQuotePath } from '@steelbuy/util';
import { RoutePath } from '../../../router/Routes';
import { FormLayout } from '../../../views/layouts/form-layout/FormLayout';
import { ListingsTable } from '../../listings-table/ListingsTable';
import { PageHeader } from '../../page-header/PageHeader';
import { useRfqFormContext } from '../RfqFormContext';

import './CreateRFQSuccess.scss';

const CARBON_STEEL_MATERIAL = { materialType: MaterialType.CARBON_STEEL };

export const CreateRFQSuccess = () => {
    const { t } = useTranslation('translation');
    const navigate = useNavigate();
    const location = useLocation();
    const isRequestQuote = isRequestQuotePath(location.pathname);
    const { reset } = useRfqFormContext();
    const { reset: wizardContextReset } = useWizardStepsContext();
    const createWizardPath = isRequestQuote ? RoutePath.CREATE_REQUEST_QUOTE_WIZARD : RoutePath.CREATE_RFQ_WIZARD;
    const {
        data: matchedListings,
        hasNextPage,
        fetchNextPage,
        isFetching,
        isFetchingNextPage,
        total,
    } = useRFQMatchedListings(location.state?.quoteId, {
        queryOptions: { enabled: isRequestQuote && !!location.state?.quoteId },
    });
    const myRFQsPath = isRequestQuote ? RoutePath.MY_REQUEST_QUOTE : RoutePath.MY_RFQS;

    const handleCreateNew = () => {
        reset();
        wizardContextReset();
        navigate(createWizardPath);
    };

    useEffect(() => {
        if (!location.state?.quoteReferenceNumber) {
            navigate(createWizardPath);
        }
    }, []);

    if (!location.state?.quoteReferenceNumber) {
        return null;
    }

    const referenceNumberText = (
        <>
            {t('application.createRFQSuccess.referenceNumber')}
            <span className="reference-number">{location.state.quoteReferenceNumber}</span>
        </>
    );

    return (
        <>
            <PageHeader pageTitle={t('application.createRFQSuccess.header')} />
            <div className="create-rfq-success">
                <FormLayout>
                    <NotificationIllustrated
                        alignment={NotificationIllustratedAlignment.VERTICAL}
                        imgSrc="/assets/lib-ui-primitive/images/ListingReview-Success.svg"
                        header={t('application.createRFQSuccess.notificationMessage')}
                        helperText={referenceNumberText}
                    >
                        <NotificationIllustratedItem text={t('application.createRFQSuccess.nextStepsHint')} />
                        <NotificationIllustratedItem
                            text={t('application.createRFQSuccess.nextStepReview')}
                            isChecked
                        />
                        {!isRequestQuote && (
                            <NotificationIllustratedItem
                                text={t('application.createRFQSuccess.nextStepNotification')}
                                isChecked
                            />
                        )}
                    </NotificationIllustrated>
                    <MarketingBannerContact
                        header={t('application.createRFQSuccess.marketingBannerHeader')}
                        text={t('application.createRFQSuccess.marketingBannerText')}
                    >
                        <a href={`mailto:${t('constants.emailSupport')}`}>{t('constants.emailSupport')}</a>
                    </MarketingBannerContact>
                    {isRequestQuote && isFetching && (
                        <div className="create-rfq-success__loading-empty-state-container">
                            {isFetching && <LoadingSpinner fullHeight={false} />}
                        </div>
                    )}
                    {matchedListings && matchedListings.length > 0 && (
                        <NotificationIllustrated
                            alignment={NotificationIllustratedAlignment.HORIZONTAL}
                            imgSrc="/assets/lib-ui-primitive/images/SearchedMatchesV2.svg"
                            header={t('application.createRFQSuccess.matchesHeader')}
                            HelperComponent={
                                <>
                                    <span>{t('application.createRFQSuccess.matchesDescription1')}</span>
                                    <span>{t('application.createRFQSuccess.matchesDescription2')}</span>
                                </>
                            }
                            className="create-rfq-success__matches-illustration"
                        />
                    )}
                </FormLayout>
                {matchedListings && matchedListings.length > 0 && (
                    <div className="create-rfq-success__matched-listings-table">
                        <ListingsTable
                            listings={matchedListings}
                            formData={CARBON_STEEL_MATERIAL}
                            hasNextPage={hasNextPage}
                            loadMore={fetchNextPage}
                            isFetching={isFetching}
                            isFetchingNextPage={isFetchingNextPage}
                            total={total}
                            detailsPath={RoutePath.SEARCH_LISTING_DETAILS_FROM_RFQ}
                        />
                    </div>
                )}
                <FormLayout>
                    <FormActionbar>
                        <ButtonGhostSystemOnLightM
                            label={t('application.createRFQSuccess.buttonLabelMyListings')}
                            onClick={() => navigate(myRFQsPath)}
                        />
                        <ButtonPrimary
                            label={t('application.createRFQSuccess.buttonLabelCreateNew')}
                            onClick={handleCreateNew}
                        />
                    </FormActionbar>
                </FormLayout>
            </div>
        </>
    );
};
